import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { getGroupPosts } from "../../functions/post";
import Post from "../post";
import CreateGroupPostPopup from "../createGroupPostPopup";
import "./style.css";

export default function GroupPosts({ group, onBack }) {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreatePost, setShowCreatePost] = useState(false);

  const loadGroupPosts = useCallback(async (isMounted = () => true) => {
    try {
      if (!isMounted()) return;
      setLoading(true);
      setError("");
      const response = await getGroupPosts(group.id, user.token);
      
      if (!isMounted()) return;
      
      if (response.status === "ok") {
        setPosts(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      if (isMounted()) {
        setError("Failed to load group posts");
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [group?.id, user.token]);

  useEffect(() => {
    let mounted = true;
    
    const loadPostsSafe = async () => {
      if (group?.id && mounted) {
        await loadGroupPosts(() => mounted);
      }
    };
    
    loadPostsSafe();
    
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id, loadGroupPosts]);

  const handlePostCreated = (newPost) => {
    setPosts([newPost, ...posts]);
  };

  if (!group) {
    return <div className="group-posts-error">Group not found</div>;
  }

  return (
    <div className="group-posts">
      <div className="group-posts-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5m7-7l-7 7 7 7"/>
          </svg>
          Back to Groups
        </button>
        <div className="group-info">
          <div className="group-header-picture">
            <img 
              src={group.groupPicture || '/images/default_group.svg'} 
              alt={`${group.name} group`}
              className="group-header-img"
            />
          </div>
          <h2>{group.name}</h2>
        </div>
      </div>

      <div className="create-group-post">
        <div className="create-post-header">
          <img src={user.picture} alt="" className="create-post-avatar" />
          <button 
            className="create-post-input"
            onClick={() => setShowCreatePost(true)}
          >
            What's on your mind, {user.first_name}?
          </button>
        </div>
        <div className="create-post-actions">
          <button 
            className="create-post-btn"
            onClick={() => setShowCreatePost(true)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <path d="M21 15l-5-5L5 21"/>
            </svg>
            Photo/Video
          </button>
          <button 
            className="create-post-btn"
            onClick={() => setShowCreatePost(true)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 19l7-7 3 3-7 7-3-3z"/>
              <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
              <path d="M2 2l7.586 7.586"/>
              <circle cx="11" cy="11" r="2"/>
            </svg>
            Feeling/Activity
          </button>
        </div>
      </div>

      {error && (
        <div className="group-posts-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="group-posts-loading">
          <div className="loading-spinner"></div>
          <p>Loading posts...</p>
        </div>
      ) : (
        <div className="group-posts-list">
          {posts.length > 0 ? (
            posts.map((post) => (
              <Post 
                key={post._id} 
                post={post} 
                user={user} 
                isGroupPost={true}
                groupName={group.name}
              />
            ))
          ) : (
            <div className="no-posts">
              <div className="no-posts-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <h3>No posts yet</h3>
              <p>Be the first to share something in {group.name}!</p>
              <button 
                className="blue_btn"
                onClick={() => setShowCreatePost(true)}
              >
                Create First Post
              </button>
            </div>
          )}
        </div>
      )}

      {showCreatePost && (
        <CreateGroupPostPopup
          user={user}
          setVisible={setShowCreatePost}
          groupId={group.id}
          groupName={group.name}
          onPostCreated={handlePostCreated}
        />
      )}
    </div>
  );
} 